import React from 'react';
import moment from 'moment';
import { Virtuoso } from 'react-virtuoso';

import { Avatar, FileBox, MCButton, Spinner } from 'src/components/basic';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';

import { getAge, lang, remCss, userFullName } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';

export class PatientInfoPanel extends BaseComponent {
  componentDidMount() {
    const model = this.getModel();
    model && model.baseSubscribe(this.subscription);
  }

  componentWillUnmount() {
    const model = this.getModel();
    model && model.unsubscribe(this.subscription);
  }

  getModel = () => this.props.model;

  render() {
    const model = this.getModel();
    const { template = 1, patient, closeBtn, hideHeader = false } = this.props;

    let lastTreatment =
      (patient.treatments && patient.treatments[0] && patient.treatments[0].data) || {};

    const loading = model && model.refreshing();
    const items =
      (model && model.items(template !== 2 && patient.id + ':recent').filter((i) => !!i.uri)) || [];
    const searchQuery = model && model.listInput('search');
    const noResults = (model && !model.refreshing() && !items.length) || !model;

    const templateHtml = {
      1: (
        <>
          <Avatar
            width={100}
            height={100}
            text={userFullName(patient)}
            onlineState={(patient.online || patient.partnerOnline) > 0 ? 'online' : ''}
            image={(patient?.avatar || '').replace('/full_', '/350_')}
          />
          <div className={'mc-flex-10-col'}>
            <div className={'mc-value-520'}>{userFullName(patient)}</div>
            <pre className={'mc-label-416'}>{patient.address}</pre>
          </div>
          <div className={'mc-flex-15-col'}>
            <div className={'mc-flex'}>
              <div className={'mc-label-416 mc-grow'}>{lang.t(LANG_CONST.age)}</div>
              <div className={'mc-value-716'}>
                {patient.birthday ? getAge(patient.birthday, true) : ''}
              </div>
            </div>
            <div className={'mc-flex'}>
              <div className={'mc-label-416 mc-grow'}>{lang.t(LANG_CONST.birthday)}</div>
              <div className={'mc-value-716'}>
                {!!patient.birthday &&
                  moment(
                    patient.birthday,
                    patient.birthday.toString().length === 8 ? 'YYYYMMDD' : ''
                  ).format('MMMM DD, YYYY')}
              </div>
            </div>
            {this.props.freeChatEl}
            <div className={'mc-flex'} style={{ minWidth: 0 }}>
              <div className={'mc-label-416 mc-grow'}>{lang.t(LANG_CONST.diagnosis)}</div>
              <pre className={'mc-value-716 mc-text-truncate'}>{lastTreatment.diagnosis}</pre>
            </div>
          </div>
        </>
      ),
      2: (
        <>
          <div className={'mc-flex-15-col mc-wrap'}>
            <Avatar image={patient.avatar} text={userFullName(patient)} width={80} height={80} />
            <div className={'mc-flex-5-col'}>
              <div className={'mc-value-622'}>{userFullName(patient)}</div>
              <div className={'mc-label-416'}>{patient.email}</div>
            </div>
          </div>
          <div className={'mc-flex-col mc-grow'}>
            <div className={'mc-label-416'}>{lang.t(LANG_CONST.gender)}</div>
            <div className={'mc-value-518'}>
              {patient.gender ? lang.t(LANG_CONST.constants.gender[patient.gender]) : ''}
            </div>
          </div>
          <div className={'mc-flex-col mc-grow'}>
            <div className={'mc-label-416 mc-grow'}>{lang.t(LANG_CONST.age)}</div>
            <div className={'mc-value-518'}>
              {patient.birthday ? getAge(patient.birthday, true) : ''}
            </div>
          </div>
          <div className={'mc-flex-col mc-grow'}>
            <div className={'mc-label-416'}>{lang.t(LANG_CONST.birthday)}</div>
            <div className={'mc-value-518'}>
              {patient.birthday
                ? moment(
                    patient.birthday,
                    patient.birthday.toString().length === 8 ? 'YYYYMMDD' : ''
                  ).format('MMMM DD, YYYY')
                : ''}
            </div>
          </div>
          {this.props.freeChatEl}
        </>
      )
    };

    return patient ? (
      <div className='mc-patient-info'>
        {!hideHeader && (
          <div className={'mc-header'}>
            <div className={'mc-flex'}>
              <div className={'mc-pointer mc-flex'} onClick={closeBtn}>
                <SVGIcon width={30} iconname={BASE_ICONS.CLOSE} />
              </div>
              <div className={'mc-title'}>{lang.t(LANG_CONST.titles.patient_info)}</div>
            </div>
            <div className={'mc-bt mc-mt15'} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <Virtuoso
            components={{
              Header: () => (
                <div className={'mc-flex-20-col mc-p20'}>
                  {templateHtml[template]}
                  <MCButton light primary radius medium onClick={this.props.viewProfile}>
                    {lang.t(LANG_CONST.buttons.view_profile)}
                  </MCButton>
                  {model && (
                    <div className={'mc-value-622 mc-pt20 mc-bt'}>
                      {lang.t(LANG_CONST.shared_files)}
                    </div>
                  )}
                  {model && (noResults || loading) && (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                      {noResults && !searchQuery && (
                        <div>{lang.t(LANG_CONST.text.no_results_yet)}</div>
                      )}
                      {noResults && searchQuery && (
                        <div>{lang.t(LANG_CONST.text.nothing_found)}</div>
                      )}
                    </div>
                  )}
                </div>
              ),
              Footer: () => <div style={{ textAlign: 'center' }}>{loading && <Spinner />}</div>
            }}
            data={items}
            endReached={() => {
              if (model && model.hasMore() && !model.refreshing()) model.loadMore();
            }}
            itemContent={(index, media) => (
              <div className={'mc-pb15 mc-pl20 mc-pr20'} key={media.counter}>
                <FileBox
                  list={items}
                  key={'mc-media-' + index}
                  file={media}
                  onClose={null}
                  template={2}
                  className={'mc-bg-primary-light mc-p10 mc-radius-5'}
                  styles={{ width: remCss(50), height: remCss(50) }}
                  onlyPreview={true}
                  showFileName={true}
                  showFileSize={true}
                />
              </div>
            )}
          />
        </div>
      </div>
    ) : null;
  }
}
